import { NodeProgram, ProgramInfo } from "sigma/rendering";
import { RenderParams } from "sigma/types";
import { floatColor } from "sigma/utils";

import { CustomNodeDisplayData } from "../../core/appearance/types";
import { fragmentShaderSource as FRAGMENT_SHADER_SOURCE } from "./program.frag";
import { vertexShaderSource as VERTEX_SHADER_SOURCE } from "./program.vert";

const { UNSIGNED_BYTE, FLOAT } = WebGLRenderingContext;

const UNIFORMS = ["u_sizeRatio", "u_correctionRatio", "u_matrix"] as const;

export default class NodeProgramBorder extends NodeProgram<(typeof UNIFORMS)[number]> {
  static readonly ANGLE_1 = 0;
  static readonly ANGLE_2 = (2 * Math.PI) / 3;
  static readonly ANGLE_3 = (4 * Math.PI) / 3;

  getDefinition() {
    return {
      VERTICES: 3,
      VERTEX_SHADER_SOURCE,
      FRAGMENT_SHADER_SOURCE,
      METHOD: WebGLRenderingContext.TRIANGLES,
      UNIFORMS,
      ATTRIBUTES: [
        { name: "a_position", size: 2, type: FLOAT },
        { name: "a_size", size: 1, type: FLOAT },
        { name: "a_color", size: 4, type: UNSIGNED_BYTE, normalized: true },
        { name: "a_borderColor", size: 4, type: UNSIGNED_BYTE, normalized: true },
        { name: "a_id", size: 4, type: UNSIGNED_BYTE, normalized: true },
      ],
      CONSTANT_ATTRIBUTES: [{ name: "a_angle", size: 1, type: FLOAT }],
      CONSTANT_DATA: [[NodeProgramBorder.ANGLE_1], [NodeProgramBorder.ANGLE_2], [NodeProgramBorder.ANGLE_3]],
    };
  }

  processVisibleItem(nodeIndex: number, startIndex: number, data: CustomNodeDisplayData) {
    const array = this.array;

    const color = floatColor(data.color);
    const borderColor = floatColor(data.borderColor || data.color);

    array[startIndex++] = data.x;
    array[startIndex++] = data.y;
    array[startIndex++] = data.size;
    array[startIndex++] = color;
    array[startIndex++] = borderColor;
    array[startIndex++] = nodeIndex;
  }

  setUniforms(params: RenderParams, { gl, uniformLocations }: ProgramInfo): void {
    const { u_sizeRatio, u_correctionRatio, u_matrix } = uniformLocations;

    gl.uniform1f(u_correctionRatio, params.correctionRatio);
    gl.uniform1f(u_sizeRatio, params.sizeRatio);
    gl.uniformMatrix3fv(u_matrix, false, params.matrix);
  }
}
